import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import {
  getSubscriptionFeaturesLeftDetails,
  getSubscriptionTrialStatus as getSubscriptionTrialStatusApi,
} from "@/api/subscription";
import {
  SubscriptionOfferings,
  SubscriptionPlanTrialStatus,
  SubscriptionFeaturesLeft,
  CustomerInfo,
  SubscriptionPlan,
} from "@/types/subscription";
import purchases from "@/lib/purchases";
import { SUBSCRIPTION_PLANS } from "@/constants";

interface SubscriptionState {
  offerings: SubscriptionOfferings | null;
  customerInfo: CustomerInfo | null;
  subscription: SubscriptionPlan;
  currentPlan: string | null;
  upcomingPlan: string | null;
  purchasedAt: string;
  store: string | null;
  identifier: string | null;
  productIdentifier: string | null;
  error: string | null;
  isSubscriptionCancelled: boolean;
  isLoading: boolean;
}

const initialState: SubscriptionState = {
  offerings: null,
  customerInfo: null,
  currentPlan: null,
  upcomingPlan: null,
  purchasedAt: "",
  store: null,
  identifier: null,
  productIdentifier: null,
  error: null,
  isSubscriptionCancelled: false,
  isLoading: false,
  subscription: {
    customer_id: "",
    subscription_id: "",
    plan_name: "",
    store: "",
    starts_at_ms: 0,
    expiration_at_ms: 0,
    msLeft: 0,
    daysLeft: 0,
    features: {
      job_post: 0,
      shortlist: 0,
    },
    product_change_details: {
      new_entitlement_id: "",
      new_product_id: "",
    },
    have_trial: false,
    is_trialing: false,
    is_cancelled: false,
    is_active: false,
    featuresLeft: {
      apply_request_left: 0,
      job_post_left: 0,
    },
  },
};

export const getSubscriptionFeaturesLeft = createAsyncThunk(
  "purchases/subscription/getSubscriptionFeaturesLeft",
  async (organizationId: string) => {
    try {
      const response = await getSubscriptionFeaturesLeftDetails(organizationId);
      return response?.result;
    } catch (error) {
      throw error;
    }
  },
);

export const getSubscriptionTrialStatus = createAsyncThunk(
  "purchases/subscription/getSubscriptionTrialStatus",
  async (organizationId: string) => {
    try {
      const response = await getSubscriptionTrialStatusApi(organizationId);
      return response?.result;
    } catch (error) {
      throw error;
    }
  },
);

export const getPurchasesOfferings = createAsyncThunk(
  "purchases/subscription/getPurchasesOfferings",
  async () => {
    try {
      return await purchases.getOfferings();
    } catch (error) {
      throw error;
    }
  },
);

export const getPurchasesCustomerInfo = createAsyncThunk(
  "purchases/subscription/getPurchasesCustomerInfo",
  async () => {
    try {
      return await purchases.getCustomerInfo();
    } catch (error) {
      throw error;
    }
  },
);

const purchasesSlice = createSlice({
  name: "purchases",
  initialState,
  reducers: {
    setSubscriptionData: (state, action: PayloadAction<SubscriptionPlan>) => {
      state.subscription = action.payload;
    },
    setPurchasesOfferings: (state, action: PayloadAction<SubscriptionOfferings>) => {
      state.offerings = action.payload;
    },
    setPurchasesCustomerInfo: (state, action: PayloadAction<CustomerInfo>) => {
      state.customerInfo = action.payload;

      SUBSCRIPTION_PLANS?.map((plan) => {
        Object.entries(action?.payload?.entitlements?.active)?.map(([key, value]) => {
          if (plan.key === key) {
            state.currentPlan = plan.title;
            state.store = value.store;
            state.purchasedAt = value.latestPurchaseDate;
            state.identifier = plan.key;
            state.productIdentifier = value.productIdentifier;
            state.isSubscriptionCancelled = value.unsubscribeDetectedAt !== null;
          }
        });
      });
    },
    setPurchasesLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUpcomingPlan: (state, action: PayloadAction<SubscriptionPlan | null>) => {
      state.upcomingPlan = action.payload?.product_change_details?.new_entitlement_id;
    },
    clearSubscriptions: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionFeaturesLeft.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getSubscriptionFeaturesLeft.fulfilled,
        (state, action: PayloadAction<SubscriptionFeaturesLeft>) => {
          state.isLoading = false;
        },
      )
      .addCase(getSubscriptionFeaturesLeft.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch subscription features left.";
      })
      .addCase(getSubscriptionTrialStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getSubscriptionTrialStatus.fulfilled,
        (state, action: PayloadAction<SubscriptionPlanTrialStatus>) => {
          state.isLoading = false;
        },
      )
      .addCase(getSubscriptionTrialStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch subscription trial status.";
      })
      .addCase(getPurchasesOfferings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPurchasesOfferings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.offerings = action.payload;
      })
      .addCase(getPurchasesOfferings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch purchases offerings data.";
      })
      .addCase(getPurchasesCustomerInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPurchasesCustomerInfo.fulfilled, (state, action: PayloadAction<CustomerInfo>) => {
        state.isLoading = false;
        state.customerInfo = action.payload;

        SUBSCRIPTION_PLANS?.map((plan) => {
          Object.entries(action.payload.entitlements.active).map(([key, value]) => {
            if (plan.key === key) {
              state.currentPlan = plan.title;
              state.store = value.store;
              state.identifier = plan.key;
              state.productIdentifier = value.productIdentifier;
              state.isSubscriptionCancelled = value.unsubscribeDetectedAt !== null;
            }
          });
        });
      })
      .addCase(getPurchasesCustomerInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Failed to fetch purchases customer info and current plan.";
      });
  },
});

export const {
  setPurchasesOfferings,
  setPurchasesCustomerInfo,
  setSubscriptionData,
  setPurchasesLoading,
  clearSubscriptions,
  setUpcomingPlan,
} = purchasesSlice.actions;

export default purchasesSlice.reducer;
